import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  alpha,
  useTheme,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Assignment as AnalystIcon,
  Engineering as EngineerIcon,
  AttachMoney as MoneyIcon,
  Security as SecurityIcon,
  Sell as SellIcon,
  BugReport as DebugIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import api from '../services/api';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../contexts/AuthContext';

const UserManagement = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // States
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userDialog, setUserDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [debugDialog, setDebugDialog] = useState(false);
  const [regions, setRegions] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    status: 'active',
    password: '',
    confirm_password: '',
    active: true,
    regions: []
  });

  // Fetch regions data
  const fetchRegions = useCallback(async () => {
    try {
      const regionsResponse = await api.get('/api/auth/regions');
      const regionsData = regionsResponse.data.regions || [];
      setRegions(regionsData);
    } catch (err) {
      console.error('Error fetching regions:', err);
    }
  }, []);

  // Fetch users data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch users
      const usersResponse = await api.get('/api/auth/users');
      const userData = usersResponse.data.users || usersResponse.data;
      setUsers(userData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...users];
    
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    if (statusFilter) {
      filtered = filtered.filter(user => 
        user.active ? 'active' : 'inactive' === statusFilter
      );
    }
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
    fetchRegions();
  }, [fetchData, fetchRegions]);

  // Handle form data change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle regions change (multi-select)
  const handleRegionsChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      regions: typeof value === 'string' ? value.split(',') : value
    });
  };

  // Open user dialog for add/edit
  const handleOpenUserDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        username: user.username,
        email: user.email || '',
        role: user.role,
        password: '',
        confirm_password: '',
        active: user.active,
        regions: user.regions || []
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: '',
        email: '',
        role: '',
        password: '',
        confirm_password: '',
        active: true,
        regions: []
      });
    }
    setUserDialog(true);
  };

  // Close user dialog
  const handleCloseUserDialog = () => {
    setUserDialog(false);
    setSelectedUser(null);
  };

  // Save user form
  const handleSaveUser = async () => {
    // Validation
    if (!formData.username || !formData.role) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (!selectedUser && (!formData.password || formData.password !== formData.confirm_password)) {
      setError('Passwords do not match');
      return;
    }
    
    if (selectedUser && formData.password && formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      if (selectedUser) {
        // Update existing user
        await api.put(`/api/auth/users/${selectedUser.username}`, {
          role: formData.role,
          ...(formData.password ? { password: formData.password } : {}),
          active: formData.active,
          email: formData.email || '',
          regions: formData.regions || []
        });
      } else {
        // Add new user
        await api.post('/api/auth/users', {
          username: formData.username,
          role: formData.role,
          password: formData.password,
          active: formData.active,
          email: formData.email || '',
          regions: formData.regions || []
        });
      }
      
      // Refresh the user list
      fetchData();
      setUserDialog(false);
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.message || 'Failed to save user');
      setLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialog(true);
  };

  // Delete user
  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      await api.delete(`/api/auth/users/${selectedUser.username}`);
      fetchData();
      setDeleteDialog(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
      setLoading(false);
    }
  };

  // Get role icon and color
  const getRoleDetails = (role) => {
    switch (role) {
      case 'admin':
        return { 
          icon: <AdminIcon />, 
          color: theme.palette.error.main,
          label: 'Admin'
        };
      case 'finance':
        return { 
          icon: <MoneyIcon />, 
          color: theme.palette.success.main,
          label: 'Finance'
        };
      case 'soc':
        return { 
          icon: <SecurityIcon />, 
          color: theme.palette.warning.main,
          label: 'SOC'
        };
      case 'rd':
        return { 
          icon: <EngineerIcon />, 
          color: theme.palette.info.main,
          label: 'R&D'
        };
      case 'sales':
        return { 
          icon: <SellIcon />, 
          color: theme.palette.primary.main,
          label: 'Sales'
        };
      case 'user':
        return { 
          icon: <PersonIcon />, 
          color: theme.palette.text.secondary,
          label: 'User'
        };
      default:
        return { 
          icon: <PersonIcon />, 
          color: theme.palette.text.secondary,
          label: role.charAt(0).toUpperCase() + role.slice(1)
        };
    }
  };

  // Format date with time
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Determine if a user is active based on last login
  const getUserStatus = (username) => {
    const user = users.find(u => u.username === username);
    return user?.active ? 'active' : 'inactive';
  };

  // Get the last login time
  const getLastLoginTime = (username) => {
    const user = users.find(u => u.username === username);
    return user?.last_login ? formatDateTime(user.last_login) : "-";
  };

  // Close error message
  const handleCloseError = () => {
    setError(null);
  };

  // Dashboard Cards - Updated counts
  const getActiveUsersCount = () => {
    return users.filter(user => user.active).length;
  };
  
  const getInactiveUsersCount = () => {
    return users.filter(user => !user.active).length;
  };

  // Test direct API call
  const testUsersApi = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/auth/users');
      console.log('Direct API response:', response.data);
      
      // Ensure the response structure matches your API and extract users
      const userData = response.data.users || response.data;
      
      // Set users directly
      if (Array.isArray(userData)) {
        setUsers(userData);
        setFilteredUsers(userData);
        setError(null);
      } else {
        setError("Invalid response format. Check the debug output.");
      }
    } catch (err) {
      console.error('Error in direct test:', err);
      setError(`API Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Toggle user active status
  const toggleUserStatus = async (username, currentStatus) => {
    if (username === 'admin') {
      setError('Cannot deactivate admin user');
      return;
    }
    
    setLoading(true);
    try {
      const newStatus = !currentStatus;
      await api.put(`/api/auth/users/${username}/status`, {
        active: newStatus
      });
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.username === username 
            ? { ...user, active: newStatus } 
            : user
        )
      );
      
      // Show success message
      setError(null);
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError(`Failed to update user status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* Page Header with Gradient Background */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: '12px',
          p: 3,
          mb: 3,
          color: '#fff',
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <PersonIcon fontSize="large" />
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight="bold">
              User Management
            </Typography>
            <Typography variant="body1">
              Add, edit, and manage user accounts and permissions
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenUserDialog()}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: alpha('#ffffff', 0.9),
                },
                borderRadius: '8px',
                boxShadow: `0 4px 8px ${alpha('#000', 0.15)}`,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Add User
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading Indicator */}
      {loading && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            my: 4 
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}

      {/* Dashboard Cards */}
      {!loading && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
                  height: '100%',
                  background: alpha(theme.palette.primary.main, 0.05)
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="medium" color="text.secondary">
                      Total Users
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PersonIcon color="primary" />
                    </Box>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {users.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
                  height: '100%',
                  background: alpha(theme.palette.success.main, 0.05)
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="medium" color="text.secondary">
                      Active Users
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckIcon color="success" />
                    </Box>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {getActiveUsersCount()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
                  height: '100%',
                  background: alpha(theme.palette.error.main, 0.05)
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="medium" color="text.secondary">
                      Admins
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AdminIcon color="error" />
                    </Box>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {users.filter(u => u.role === 'admin').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`,
                  height: '100%',
                  background: alpha(theme.palette.info.main, 0.05)
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="medium" color="text.secondary">
                      Inactive Users
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BlockIcon color="info" />
                    </Box>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {getInactiveUsersCount()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Filters */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search users..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: '8px' }
                }}
                sx={{ minWidth: 200, flex: 1 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="Role"
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="soc">SOC</MenuItem>
                  <MenuItem value="rd">R&D</MenuItem>
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                sx={{ 
                  borderRadius: '8px', 
                  textTransform: 'none',
                  ml: 1
                }}
              >
                Refresh
              </Button>
            </Box>
          </Paper>

          {/* Users Table */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: `0 4px 12px ${alpha('#000', 0.1)}`
            }}
          >
            <Table>
              <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Regions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Login</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const roleDetails = getRoleDetails(user.role);
                    const userStatus = getUserStatus(user.username);
                    return (
                      <TableRow key={user._id || user.username} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: alpha(roleDetails.color, 0.1),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: roleDetails.color,
                                mr: 2
                              }}
                            >
                              {user.username?.charAt(0).toUpperCase() || '?'}
                            </Box>
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {user.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={roleDetails.icon}
                            label={roleDetails.label}
                            size="small"
                            sx={{
                              bgcolor: alpha(roleDetails.color, 0.1),
                              color: roleDetails.color,
                              fontWeight: 500,
                              borderRadius: '8px'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {user.regions && user.regions.length > 0 ? (
                              user.regions.map((region, idx) => (
                                <Chip
                                  key={idx}
                                  label={region}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.1),
                                    color: theme.palette.info.main,
                                    fontSize: '0.7rem',
                                    height: '20px'
                                  }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={userStatus === 'active' ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              bgcolor: userStatus === 'active' 
                                ? alpha(theme.palette.success.main, 0.1) 
                                : alpha(theme.palette.error.main, 0.1),
                              color: userStatus === 'active' 
                                ? theme.palette.success.main 
                                : theme.palette.error.main,
                              fontWeight: 500,
                              borderRadius: '8px'
                            }}
                          />
                        </TableCell>
                        <TableCell>{getLastLoginTime(user.username)}</TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit User">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenUserDialog(user)}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={user.active ? "Deactivate User" : "Activate User"}>
                              <IconButton 
                                size="small" 
                                onClick={() => toggleUserStatus(user.username, user.active)}
                                sx={{ color: user.active ? theme.palette.success.main : theme.palette.error.main }}
                                disabled={user.username === 'admin'}
                              >
                                {user.active ? 
                                  <ToggleOnIcon fontSize="small" /> : 
                                  <ToggleOffIcon fontSize="small" />
                                }
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete User">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenDeleteDialog(user)}
                                sx={{ color: theme.palette.error.main }}
                                disabled={user.username === 'admin'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <PersonIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.primary, 0.3), mb: 1 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          No users found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {users.length ? 'Try adjusting your filters' : 'Add a new user to get started'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog 
        open={userDialog} 
        onClose={handleCloseUserDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: `0 8px 24px ${alpha('#000', 0.2)}`
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedUser ? 'Edit User' : 'Add New User'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                variant="outlined"
                disabled={!!selectedUser}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                variant="outlined"
                helperText="Email address for license expiration notifications"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  label="Role"
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="finance">Finance</MenuItem>
                  <MenuItem value="soc">SOC</MenuItem>
                  <MenuItem value="rd">R&D</MenuItem>
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel>Regions</InputLabel>
                <Select
                  name="regions"
                  multiple
                  value={formData.regions || []}
                  onChange={handleRegionsChange}
                  label="Regions"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {regions.map((region) => (
                    <MenuItem key={region.code || region} value={region.code || region}>
                      {region.display || region.code || region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={selectedUser ? "New Password (leave blank to keep current)" : "Password"}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                variant="outlined"
                required={!selectedUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Password"
                name="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                variant="outlined"
                required={!selectedUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  name="active"
                  value={formData.active}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseUserDialog}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser}
            variant="contained"
            color="primary"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            {selectedUser ? 'Update User' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete user <strong>{selectedUser?.username}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteDialog(false)}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug Dialog */}
      <Dialog
        open={debugDialog}
        onClose={() => setDebugDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle>API Response Debug</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Users Data:</Typography>
          <Paper sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto' }}>
            <pre>{JSON.stringify(users, null, 2)}</pre>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDebugDialog(false)}
            variant="contained"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 